import { useEffect, useRef } from "react";

/**
 * Custom hook that enables drag-to-scroll functionality on a scrollable element.
 * - Dragging is only enabled when the content actually overflows.
 * - Clicks are suppressed if a drag occurred, preventing accidental activation
 *   of clickable child elements (e.g. cards) during drag.
 * Returns a ref to be attached to the element you want to make draggable.
 *
 * @example
 * const scrollRef = useDragScroll();
 * return <div ref={scrollRef} style={{ overflow: 'auto' }}>...</div>
 */
const useDragScroll = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const startYRef = useRef(0);
  const hasDraggedRef = useRef(false);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Helper function to check if element has overflow based on actual content
    const checkHasOverflow = (): boolean => {
      const children = Array.from(element.children) as HTMLElement[];
      if (children.length === 0) return false;
      
      const lastChild = children[children.length - 1];
      const contentWidth = lastChild.offsetLeft + lastChild.offsetWidth;
      return contentWidth > element.clientWidth;
    };

    // Check overflow and update cursor
    const updateOverflowState = () => {
      requestAnimationFrame(() => {
        if (!element) return;
        
        const hasOverflow = checkHasOverflow();
        
        element.style.cursor = hasOverflow ? "grab" : "";
      });
    };

    // Check overflow on mount and when content changes
    const timeoutId = setTimeout(updateOverflowState, 100);
    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(element);

    const handleMouseDown = (e: MouseEvent) => {
      if (!checkHasOverflow()) return;
      
      isDraggingRef.current = true;
      startXRef.current = e.pageX - element.offsetLeft;
      startYRef.current = e.pageY;
      hasDraggedRef.current = false;
      suppressClickRef.current = false;
      scrollLeftRef.current = element.scrollLeft;
      
      element.style.cursor = "grabbing";
      element.style.userSelect = "none";
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      
      e.preventDefault();
      const x = e.pageX - element.offsetLeft;
      const walk = (x - startXRef.current) * 1.5;
      element.scrollLeft = scrollLeftRef.current - walk;

      const deltaX = Math.abs(e.pageX - (startXRef.current + element.offsetLeft));
      const deltaY = Math.abs(e.pageY - startYRef.current);
      if (deltaX > 5 || deltaY > 5) {
        hasDraggedRef.current = true;
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      if (hasDraggedRef.current) {
        suppressClickRef.current = true;
        // allow click suppression for the immediate click event
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }
      element.style.cursor = checkHasOverflow() ? "grab" : "";
      element.style.userSelect = "";
    };

    const handleMouseLeave = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        if (hasDraggedRef.current) {
          suppressClickRef.current = true;
          setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
        }
        element.style.cursor = checkHasOverflow() ? "grab" : "";
        element.style.userSelect = "";
      }
    };

    const handleClickCapture = (e: MouseEvent) => {
      if (suppressClickRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add event listeners
    element.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    element.addEventListener("mouseleave", handleMouseLeave);
    element.addEventListener("click", handleClickCapture, true);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      element.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      element.removeEventListener("mouseleave", handleMouseLeave);
      element.removeEventListener("click", handleClickCapture, true);
    };
  }, []);

  return ref;
};

export default useDragScroll;
